import { useApp } from "@/AppProvider"
import { useFilterViews, useUpdateFilterView } from "@/hooks/useFilterViews"
import { useLocalStorage } from "@/hooks/useLocalStorage"
import { Popover, Transition } from "@headlessui/react"
import { BookmarkIcon, FunnelIcon, PencilSquareIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { FC, Fragment, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import Select, { StylesConfig } from "react-select"
import { classNames } from "../../../utils/classNames"
import Modal from "../Modal"
import { FilterViewForm } from "./FilterViewForm"

const OPERATORS = ["is", "is not"]

export interface VariableFilter {
  key: string
  operator: string
  value: string
}

export interface FilterOption {
  key: string
  title: string
  values: any[]
}

interface FiltersProps {
  viewName: string
  filters: VariableFilter[]
  setFilters: (vars: VariableFilter[]) => void
  filterOptions: FilterOption[]

  // Customization
  /** Show the current filters. If false, shows the number of filters */
  showFilters?: boolean
  buttonClassName?: string
}

export const Filters: FC<FiltersProps> = ({
  viewName,
  filters,
  setFilters,
  filterOptions,
  showFilters = false,
  buttonClassName = "",
}) => {
  const { token } = useApp()
  const { organizationId } = useParams()

  // Use local storage to persist filters for this view
  const [persistedFilters, setPersistedFilters] = useLocalStorage<VariableFilter[]>(
    `filters_${viewName}_${organizationId}`,
    [],
  )

  // Use local storage to persist the selected filter view ID
  const [selectedViewId, setSelectedViewId] = useLocalStorage<string | null>(
    `selectedView_${viewName}_${organizationId}`,
    null,
  )

  const { data: filterViews } = useFilterViews(organizationId, viewName, token)
  const updateFilterView = useUpdateFilterView(organizationId!, viewName, token)

  // Load persisted filters on mount if no filters are currently set
  useEffect(() => {
    if (filters.length === 0 && persistedFilters.length > 0) {
      setFilters(persistedFilters)
    }
  }, []) // Only run on mount

  // Save filters to local storage whenever they change
  useEffect(() => {
    setPersistedFilters(filters)
  }, [filters, setPersistedFilters])

  const matchingView = useMemo(() => {
    return filterViews?.find((fv) => {
      if (fv.filters.length !== filters.length) return false

      return fv.filters.every((f: any) => {
        const idx = filters.findIndex(
          (f2) => f.key === f2.key && f.operator === f2.operator && f.value === f2.value,
        )
        if (idx === -1) return false
        return true
      })
    })
  }, [filterViews, filters])
  const [filterViewOpen, setFilterViewOpen] = useState(false)
  const [viewToEdit, setViewToEdit] = useState<any>(null)

  const addFilter = () => {
    setFilters([...filters, { key: "", operator: "is", value: "" }])
  }

  const removeFilter = (idx: number) => {
    let newFilters = [...filters]
    newFilters.splice(idx, 1)
    setFilters(newFilters)
    // Clear selected view when manually modifying filters
    setSelectedViewId(null)
  }

  const updateFilter = (idx: number, prop: keyof VariableFilter, value: string) => {
    let newFilters = [...filters]
    let filter = filters[idx]

    // If key changes, clear the other values
    const isNewKey = prop === "key" && filter.key !== value
    let newValue = isNewKey ? { key: value, operator: "is", value: "" } : { ...filter, [prop]: value }
    newFilters.splice(idx, 1, newValue)
    setFilters(newFilters)
    // Clear selected view when manually modifying filters
    setSelectedViewId(null)
  }

  const options = useMemo(() => {
    const keyOptions = filterOptions.map((fo) => ({ value: fo.key, label: fo.title || fo.key }))
    const valueOptions = filterOptions.map((fo) => ({
      key: fo.key,
      values: fo.values.map((val) => ({ value: val, label: val })),
    }))
    return { keyOptions, valueOptions }
  }, [filterOptions])

  // Sort filterViews: pinned first, then alphabetically by name
  const sortedFilterViews = useMemo(() => {
    if (!filterViews) return []
    return [...filterViews].sort((a, b) => {
      const aPinned = a.pinned ? 1 : 0
      const bPinned = b.pinned ? 1 : 0
      if (bPinned !== aPinned) {
        return bPinned - aPinned // pinned first
      }
      return a.name.localeCompare(b.name)
    })
  }, [filterViews])

  // Get up to 3 pinned views
  const pinnedViews = useMemo(
    () => sortedFilterViews.filter((v) => v.pinned).slice(0, 3),
    [sortedFilterViews],
  )

  // Handle view selection with persistence
  const selectView = (view: any) => {
    if (matchingView?._id === view._id || selectedViewId === view._id) {
      setFilters([])
      setSelectedViewId(null)
    } else {
      setFilters(Array.isArray(view.filters) ? view.filters : [])
      setSelectedViewId(view._id)
    }
  }

  return (
    <>
      {pinnedViews.length > 0 && (
        <div className="flex items-center justify-center gap-2">
          {pinnedViews.map((view) => (
            <button
              key={view._id}
              className={classNames(
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                matchingView?._id === view._id || selectedViewId === view._id
                  ? "bg-primary-100 text-primary-800 border-primary-300"
                  : "hover:bg-primary-50 hover:text-primary-800 hover:border-primary-200 border-gray-200 bg-gray-100 text-gray-700",
              )}
              onClick={() => selectView(view)}>
              {view.name}
            </button>
          ))}
        </div>
      )}
      {filterViews && filterViews.length > 0 && (
        <Popover as="div" className="relative">
          {({ open }) => (
            <>
              <Popover.Button
                as="button"
                className={classNames(`btn-outline flex items-center gap-1.5`, buttonClassName)}>
                <BookmarkIcon className="h-5 w-5 text-gray-600" />
                Views
              </Popover.Button>
              <Transition
                as={Fragment}
                appear={true}
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0">
                <Popover.Panel className="absolute left-0 z-10 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black/10 focus:outline-none">
                  <div className="py-2">
                    {sortedFilterViews.map((view: any, i) => (
                      <div
                        key={i}
                        className={`flex items-center justify-between gap-2 px-4 ${matchingView?._id === view._id || selectedViewId === view._id ? "bg-primary-50" : "hover:bg-gray-50"}`}>
                        <button className="block w-full px-1 py-2 text-left" onClick={() => selectView(view)}>
                          {view.name}
                        </button>
                        <button
                          className="mr-1 rounded p-1 hover:bg-gray-100"
                          onClick={() => {
                            setViewToEdit(view)
                            setFilterViewOpen(true)
                          }}
                          aria-label="Edit view">
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            updateFilterView.mutate({ id: view._id, pinned: !view.pinned })
                          }}
                          aria-pressed={!!view.pinned}>
                          <span className="sr-only">{view.pinned ? "Remove bookmark" : "Bookmark"}</span>
                          <BookmarkIcon
                            className={`h-5 w-5 ${view.pinned ? "fill-primary-600 text-primary-600" : ""}`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
      )}
      <Popover as="div" className="relative">
        {({ open }) => (
          <>
            <div className="flex items-center gap-x-2">
              <Popover.Button
                as="button"
                className={classNames(`btn-outline flex items-center gap-1.5`, buttonClassName)}>
                <FunnelIcon className="h-5 w-5 text-gray-600" />
                Filters{" "}
                {!showFilters && filters.length > 0 ? (
                  <span className="-mr-1 flex h-5 w-5 items-center justify-center rounded-full bg-gray-100">
                    <span className="text-xs text-gray-700 tabular-nums">{filters.length}</span>
                  </span>
                ) : null}
              </Popover.Button>
              {showFilters ? (
                <div className="flex flex-wrap gap-1">
                  {filters
                    .filter((f) => !!f.key && !!f.value)
                    .map((filter, idx) => {
                      const filterOption = filterOptions.find((fo) => fo.key === filter.key)
                      const displayTitle = filterOption?.title || filter.key
                      return (
                        <div key={idx} className="badge flex space-x-1 py-1.5">
                          <span className="text-gray-900">{displayTitle}</span>
                          <span className="text-gray-500">{filter.operator}</span>
                          <span className="text-gray-900">{filter.value}</span>
                          <button
                            onClick={() => {
                              removeFilter(idx)
                            }}
                            className="hover:text-gray-900">
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </div>
                      )
                    })}
                </div>
              ) : null}
            </div>
            <Transition
              as={Fragment}
              appear={true}
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0">
              <Popover.Panel
                className={`absolute left-0 z-10 mt-2 w-[500px] rounded-md bg-white shadow-lg ring-1 ring-black/10 focus:outline-none`}
                anchor="bottom start">
                <div className="space-y-2 px-3 py-4">
                  {filters.map((filter, idx) => (
                    <div className="flex space-x-2" key={idx}>
                      <Select
                        value={options.keyOptions.find((opt) => opt.value === filter.key) || null}
                        getOptionLabel={(o: any) => o.label}
                        getOptionValue={(o: any) => o.value}
                        options={options.keyOptions}
                        onChange={(selectedOption: any) => {
                          updateFilter(idx, "key", selectedOption?.value || "")
                        }}
                        className="input flex-1 p-0"
                        placeholder=""
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      <Select
                        value={valToSelectOpt(filter.operator) as any}
                        options={OPERATORS.map((o) => ({ value: o, label: o }))}
                        onChange={({ value }: { value: string }) => {
                          updateFilter(idx, "operator", value)
                        }}
                        className="input w-[100px] p-0"
                        placeholder=""
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      <Select
                        value={valToSelectOpt(filter.value) as any}
                        options={options.valueOptions.find((f) => f.key === filter.key)?.values ?? []}
                        onChange={({ value }: { value: string }) => {
                          updateFilter(idx, "value", value)
                        }}
                        className="input flex-1 p-0"
                        placeholder=""
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      <button onClick={() => removeFilter(idx)} className="text-gray-500 hover:text-gray-900">
                        <span className="sr-only">Remove filter</span>
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-stretch gap-1">
                    <button onClick={addFilter} className="btn-outline">
                      <PlusIcon className="mr-1.5 -ml-1.5 h-5 w-5" />
                      <span className="sr-only">Add</span>
                      Filter
                    </button>
                    {filters.filter((f) => !!f.value).length > 0 ? (
                      <button
                        className="btn-text"
                        onClick={() => {
                          setFilters([])
                          setSelectedViewId(null)
                        }}>
                        Clear
                      </button>
                    ) : null}
                    {filters.filter((f) => !!f.value).length > 0 ? (
                      <button className="btn-text ml-auto" onClick={() => setFilterViewOpen(true)}>
                        {matchingView ? "Edit view" : "Save as 'View'"}
                      </button>
                    ) : null}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
            <Modal open={filterViewOpen} setOpen={setFilterViewOpen}>
              <div className="min-w-lg">
                <FilterViewForm
                  organizationId={organizationId!}
                  filters={viewToEdit?.filters ?? filters}
                  viewName={viewName}
                  onClose={() => {
                    setFilterViewOpen(false)
                    setViewToEdit(null)
                  }}
                  view={viewToEdit ?? matchingView}
                />
              </div>
            </Modal>
          </>
        )}
      </Popover>
    </>
  )
}

const selectStyles: StylesConfig = {
  control: (base, state) => ({
    ...base,
    boxShadow: "none",
    border: state.isFocused ? "1px solid rgb(79 70 229)" : "1px solid rgb(209 213 219)",
    outline: state.isFocused ? "1px solid rgb(79 70 229)" : "none",
    ":hover": {
      border: state.isFocused ? "1px solid rgb(79 70 229)" : "1px solid rgb(209 213 219)",
    },
  }),
  input: (base) => ({
    ...base,
    "input:focus": {
      boxShadow: "none",
    },
  }),
  indicatorSeparator: (base) => ({
    display: "none",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isSelected ? "rgb(79 70 229)" : isFocused ? "rgb(224 231 255)" : "",
    ":hover": {
      backgroundColor: isSelected ? "rgb(79 70 229)" : isFocused ? "rgb(224 231 255)" : "rgb(224 231 255)",
    },
  }),
}

const valToSelectOpt = (val: string) => ({ value: val, label: val })
